import type { FC } from 'react';
import { Col, Row } from 'react-bootstrap';
import {
  BoxArrowInRight,
  BoxArrowRight,
  DoorOpen,
  People,
  Speedometer2,
} from 'react-bootstrap-icons';

import type { ArrivalsDeparturesView } from '../../../../modules/interfaces/dashboard';
import { formatCount, formatPercent } from '../../../../modules/utils/dashboard';
import { StatTile } from './StatTile';

export interface KpiRowProps {
  arrivals?: ArrivalsDeparturesView;
  loading: boolean;
  /** Human-readable form of the `date` these figures are scoped to. */
  dateLabel: string;
}

const ICON_SIZE = 20;

export const KpiRow: FC<KpiRowProps> = ({ arrivals, loading, dateLabel }) => {
  const tiles = [
    {
      key: 'arrivals',
      label: 'Arrivals',
      value: arrivals ? formatCount(arrivals.arrivals) : '0',
      hint: 'Checking in',
      icon: <BoxArrowInRight size={ICON_SIZE} />,
    },
    {
      key: 'departures',
      label: 'Departures',
      value: arrivals ? formatCount(arrivals.departures) : '0',
      hint: 'Checking out',
      icon: <BoxArrowRight size={ICON_SIZE} />,
    },
    {
      key: 'inHouse',
      label: 'In house',
      value: arrivals ? formatCount(arrivals.inHouse) : '0',
      hint: 'Staying tonight',
      icon: <People size={ICON_SIZE} />,
    },
    {
      key: 'totalRooms',
      label: 'Total rooms',
      value: arrivals ? formatCount(arrivals.totalRooms) : '0',
      icon: <DoorOpen size={ICON_SIZE} />,
    },
    {
      key: 'allocation',
      label: 'Allocation',
      // `allocationPercent` is null when the property has no rooms, so this
      // renders '—' rather than a meaningless 0%.
      value: formatPercent(arrivals?.allocationPercent ?? null, 0),
      icon: <Speedometer2 size={ICON_SIZE} />,
      meterPercent: arrivals?.allocationPercent ?? null,
    },
  ];

  return (
    <>
      {/*
        Scoped to this section only, not the page header: `date` affects just the
        arrivals/departures slice. Occupancy is the whole year and ratings are
        all-time, so a page-level date would misdescribe them.
      */}
      <h2 className="dashboard-section-title">
        Today <span className="dashboard-section-title__date">· {dateLabel}</span>
      </h2>
      <Row className="g-3 dashboard-kpis">
        {tiles.map((tile) => (
          <Col key={tile.key} xs={6} md={4} xl>
            <StatTile
              label={tile.label}
              value={tile.value}
              hint={tile.hint}
              icon={tile.icon}
              loading={loading}
              meterPercent={tile.meterPercent ?? null}
            />
          </Col>
        ))}
      </Row>
    </>
  );
};
